/**
 * S-75: 管理者 MFA（TOTP）の Firebase Web SDK glue。
 *
 * UI / 純関数からは分離し、`firebase/auth` の MultiFactor API 呼び出しのみを集約する。
 * 各 app の SignInPage（challenge）/ Guard（enrollment gate）から利用する。
 *
 * 前提: GCP プロジェクトが Identity Platform にアップグレードされ、MFA（TOTP）が
 * 有効化されていること（dev / prod のコンソール / API 操作）。未有効だと
 * `startTotpEnrollment` 等が `auth/operation-not-allowed` を throw する。
 */
import { getMultiFactorResolver, multiFactor, signInWithEmailAndPassword, TotpMultiFactorGenerator, } from 'firebase/auth';
import { isMfaRequiredError } from './mfaPure.js';
/**
 * メール/パスワードでサインインを試みる。
 *
 * - 成功（MFA 未登録 or 不要） → `{ kind: 'signed-in' }`（以降は onAuthStateChanged が発火）
 * - MFA 登録済 → Firebase が `auth/multi-factor-auth-required` を throw するので
 *   `getMultiFactorResolver` で resolver を取り出して `{ kind: 'mfa-required', resolver }` を返す
 * - その他のエラー（パスワード誤り等）はそのまま throw（呼出側で mapAuthErrorMessage）
 */
export async function attemptEmailPasswordSignIn(auth, email, password) {
    try {
        await signInWithEmailAndPassword(auth, email, password);
        return { kind: 'signed-in' };
    }
    catch (err) {
        if (isMfaRequiredError(err)) {
            const resolver = getMultiFactorResolver(auth, err);
            return { kind: 'mfa-required', resolver };
        }
        throw err;
    }
}
/**
 * resolver の hints から TOTP の第二要素を選んで challenge を解決する。
 * 成功すると resolver 内部でサインインが完了し onAuthStateChanged が発火する。
 *
 * @throws TOTP の hint が無い場合 / コード不正（auth/invalid-verification-code 等）
 */
export async function resolveTotpChallenge(resolver, code) {
    const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) {
        throw new Error('TOTP の第二要素が登録されていません');
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    await resolver.resolveSignIn(assertion);
}
/**
 * TOTP enrollment を開始し、QR / 手動キーを生成する。
 *
 * @param issuer 認証アプリに表示される発行者名（例: 'TracePit'）
 * @param accountName 認証アプリに表示されるアカウント名（管理者メール推奨）
 */
export async function startTotpEnrollment(user, issuer, accountName) {
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    const qrCodeUrl = secret.generateQrCodeUrl(accountName, issuer);
    return { secret, qrCodeUrl, sharedSecretKey: secret.secretKey };
}
/**
 * 認証アプリに表示された 6 桁コードで enrollment を確定する。
 *
 * @throws コード不正（auth/invalid-verification-code 等）/ 再ログイン要求
 */
export async function completeTotpEnrollment(user, secret, code, displayName) {
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    await multiFactor(user).enroll(assertion, displayName);
}
/**
 * 指定 user が TOTP の第二要素を登録済みか。
 * 任意 (opt-in) の MFA 設定 UI で「有効/無効」表示や challenge 判定に使う。
 */
export function hasEnrolledTotp(user) {
    return multiFactor(user).enrolledFactors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
}
/**
 * 登録済みの TOTP factor（uid / displayName を含む）を返す。未登録なら null。
 * 解除 (unenroll) 時に factor uid が必要なため。
 */
export function getEnrolledTotpFactor(user) {
    return (multiFactor(user).enrolledFactors.find((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? null);
}
/**
 * TOTP の第二要素を解除する（opt-in を OFF に戻す）。
 * @param factorUid `getEnrolledTotpFactor(user)?.uid`
 * @throws 直近ログインが古い場合 `auth/requires-recent-login`
 */
export async function unenrollTotp(user, factorUid) {
    await multiFactor(user).unenroll(factorUid);
}
//# sourceMappingURL=mfa.js.map